import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IActionRoomVideosFailurePayload, IActionRoomVideosSuccessPayload, IBlobError, IBlobRequest, IBlobSuccess, IShowVideoViewer, IUploadComplete, IUploadError, IUploadPercentage, IUploadRequest, IUploadSave, IVideoRemove, IVideoRemoveFailure, IVideoSaveRequest, IVideoSaveSuccess, IVideosRequest, IVideosState } from './types'



const initialState: IVideosState = {
    roomVideos: {},
    uploads: [],
    blobs: {}
}

export const videosSlice = createSlice({
    name: 'videos',
    initialState,
    reducers: {
        // *** LIST VIDEOS ***
        roomVideosRequest: (state, action: PayloadAction<IVideosRequest>) => {
            state.roomVideos[action.payload.roomId] = {
                isLoading: true,
                data: state.roomVideos[action.payload.roomId]?.data || []
            }
        },

        roomVideosRequestSuccess: (state, action: PayloadAction<IActionRoomVideosSuccessPayload>) => {
            state.roomVideos[action.payload.roomId] = {
                isLoading: false,
                data: action.payload.data
            }
        },
        roomVideosRequestFailure: (state, action: PayloadAction<IActionRoomVideosFailurePayload>) => {
            state.roomVideos[action.payload.roomId] = {
                isLoading: false,
                data: state.roomVideos[action.payload.roomId]?.data || [],
                error: action.payload.error
            }
        },

        // *** DELETE VIDEO ***
        roomVideoRemoveRequest(state, action: PayloadAction<IVideoRemove>) {
            state.roomVideos = {
                ...state.roomVideos,
                [action.payload.roomId]: {
                    data: state.roomVideos[action.payload.roomId].data.map(video => {
                        if (video.id !== action.payload.videoId) return video;

                        return {
                            ...video,
                            isDeleting: true
                        }
                    }),
                    isLoading: false,
                    error: undefined
                }
            }
        },
        roomVideoRemoveSuccess(state, action: PayloadAction<IVideoRemove>) {
            state.roomVideos = {
                ...state.roomVideos,
                [action.payload.roomId]: {
                    data: state.roomVideos[action.payload.roomId].data.filter(video => video.id !== action.payload.videoId),
                    isLoading: false,
                    error: undefined
                }
            }
        },

        roomVideoRemoveFailure: (state, action: PayloadAction<IVideoRemoveFailure>) => {
            state.roomVideos = {
                ...state.roomVideos,
                [action.payload.roomId]: {
                    data: state.roomVideos[action.payload.roomId].data.map(video => {
                        if (video.id !== action.payload.videoId) return video;

                        return {
                            ...video,
                            isDeleting: false
                        }
                    }),
                    isLoading: false,
                    error: action.payload.error
                }
            }
        },

        // *** BLOBS *** 
        blobRequest(state, action: PayloadAction<IBlobRequest>) {
            state.blobs = {
                ...state.blobs,
                [action.payload.link]: {
                    isLoading: true
                }
            }
        },
        blobSuccess(state, action: PayloadAction<IBlobSuccess>) {
            state.blobs = {
                ...state.blobs,
                [action.payload.link]: {
                    isLoading: false,
                    blob: action.payload.blob
                }
            }
        },
        blobFailure(state, action: PayloadAction<IBlobError>) {
            state.blobs = {
                ...state.blobs,
                [action.payload.link]: {
                    isLoading: false,
                    error: action.payload.error
                }
            }
        },

        // *** UPLOAD  *** 
        fileUploadVideo: (state, action: PayloadAction<IUploadRequest>) => {
            const upload = action.payload
            state.uploads = [...state.uploads, {
                id: upload.id,
                entityId: upload.entityId,
                percentage: 0,
                file: upload.file
            }]
        },
        fileUploadSetPercentage(state, action: PayloadAction<IUploadPercentage>) {
            state.uploads = state.uploads.map(upload => {
                if (upload.id !== action.payload.id) return upload;

                return {
                    ...upload,
                    percentage: action.payload.percentage
                }
            })
        },
        fileUploadError(state, action: PayloadAction<IUploadError>) {
            state.uploads = state.uploads.map(upload => {
                if (upload.id !== action.payload.uploadId) return upload;

                return {
                    ...upload,
                    isSaving: false,
                    error: action.payload.error
                }
            })
        },
        fileUploadSavingState(state, action: PayloadAction<IUploadSave>) {
            state.uploads = state.uploads.map(upload => {
                if (upload.id !== action.payload.uploadId) return upload;

                return {
                    ...upload,
                    isSaving: action.payload.isSaving
                }
            })
        },
        fileUploadComplete(state, action: PayloadAction<IUploadComplete>) {
            state.uploads = state.uploads.filter(upload => upload.id !== action.payload.uploadId);
        },
        // *** ROOM VIDEOS SAVE ***
        roomVideoSaveRequest(state, action: PayloadAction<IVideoSaveRequest>) {
            state.uploads = state.uploads.map(upload => {
                if (upload.id !== action.payload.uploadId) return upload;

                return {
                    ...upload,
                    isSaving: true
                }
            })
        },
        roomVideoSaveSuccess(state, action: PayloadAction<IVideoSaveSuccess>) {
            const { roomId, video } = action.payload;

            if (!state.roomVideos[roomId]) {
                state.roomVideos = {
                    ...state.roomVideos,
                    [roomId]: {
                        data: [video],
                        isLoading: false,
                        error: undefined
                    }
                }
            }
            else state.roomVideos[roomId].data = [...state.roomVideos[roomId].data, video];
        },
        //SHOW PHOTOS
        showVideoViewer(state, action: PayloadAction<IShowVideoViewer>) {
            state.videosViewer = action.payload
        },
        stopShowVideoViewer(state) {
            state.videosViewer = undefined
        },
    }
})

export const {
    roomVideosRequest,
    roomVideosRequestSuccess,
    roomVideosRequestFailure,
    roomVideoRemoveRequest,
    roomVideoRemoveSuccess,
    roomVideoRemoveFailure,
    blobRequest,
    blobSuccess,
    blobFailure,
    fileUploadVideo,
    fileUploadSetPercentage,
    fileUploadError,
    fileUploadSavingState,
    fileUploadComplete,
    roomVideoSaveRequest,
    roomVideoSaveSuccess,
    showVideoViewer,
    stopShowVideoViewer
} = videosSlice.actions

export default videosSlice.reducer